// MY CLOSET (ITEM 1, REBUILT BY RAIL-9b) — **THE DRAWER'S BODY.** Her order: *"every member's
// personal spiritual inventory, so Lala and the house know what they own. Universal, not
// Conjure-exclusive."*
//
// **HER REBUILD ORDER, 2026-08-10, AFTER SHE OPENED IT:** *"the drawer currently opens onto a bare
// unlabeled add-form rendered semi-transparent over the room with the rail bleeding through."*
// Screen one is now **exactly three things** — the search bar, her item list, and one gold Add
// pinned at the bottom — **and the add flow lives BEHIND Add.**
//
// **THE CAPABILITY HER SPEC ASKS FOR WAS ALREADY ON THE SERVER AND THE DRAWER SIMPLY NEVER USED
// IT** (measured before a line was written, R25). `server/closet-routes.js` has answered
// `PATCH /api/closet/:id` and accepted a base64 `photo` on both write routes since ITEM 1 shipped;
// the old body sent neither, so **Edit and the photo were built, deployed, and unreachable.** This
// round is a client rebuild — no route, no migration, no store change.
//
// IT LIVES OUT HERE RATHER THAN IN App.tsx FOR R8's REASON: App.tsx is at its ratcheted ceiling and
// the ceiling only ever goes down. The drawer CHROME (the tab and `EdgeDrawer`) stays in App.tsx,
// because `EdgeDrawer` closes over `useEscapeKey` and `CLOSE_OVERLAYS_EVENT` and duplicating it here
// would be a second body of the law that makes every drawer on this site obey the bottom bar
// (R28/R29). **That is also what already satisfies her portal clause**: `EdgeDrawer` renders through
// `createPortal(..., document.body)`, so the panel has never been trapped by a room's stacking
// context. What was missing was never the portal — it was a BACKGROUND (see `.closet-drawer`).
//
// EVERY DECISION IT MAKES IS `src/closet.js`'s, IMPORTED AND NEVER RESTATED. The type roster, the two
// sources, the validation, the search, the newest-first order, the five list states and the row's
// face are the same functions the SERVER validates writes with — a second copy on this side would be
// a picker that offers what the store refuses (R15's law, which has already cost a whole upload).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Droplet, Flame, Leaf, Sprout, Gem, Stamp, Wind, Church, Wrench, Package, HelpCircle,
} from "lucide-react";
import {
  CLOSET_TYPES, CLOSET_SOURCE_CHOICES, CLOSET_SOURCE_STEP_TITLE, CLOSET_FORM_TITLE,
  CLOSET_SEARCH_PLACEHOLDER, CLOSET_ADD_LABEL, CLOSET_EMPTY_LINE,
  CLOSET_NAME_MAX, CLOSET_NOTE_MAX, CLOSET_SAVE_LABEL, CLOSET_BACK_LABEL, CLOSET_EDIT_LABEL,
  CLOSET_DELETE_LABEL, CLOSET_PHOTO_LABEL, CLOSET_NAME_LABEL, CLOSET_TYPE_LABEL,
  CLOSET_NO_MATCH_LINE, CLOSET_WAITING_LINE, CLOSET_FAILED_LINE,
  CLOSET_DESCRIPTION_LABEL, CLOSET_DESCRIPTION_PROMPT, CLOSET_DESCRIPTION_ENCOURAGE,
  CLOSET_CONJURE_PLACEHOLDER, CLOSET_BROWSE_LABEL, CLOSET_BROWSE_TITLE, CLOSET_BROWSE_BACK,
  CLOSET_BULK_MAX, CLOSET_BULK_ENTER, CLOSET_BULK_LEAVE, CLOSET_BULK_NONE, closetBulkAddLabel,
  CLOSET_SUGGEST_SEARCHING, CLOSET_NOT_LISTED_LABEL, CLOSET_NO_PRODUCT_LINE, CLOSET_SUGGEST_MIN,
  closetTypeLabel, closetSourceLabel, closetSearch, closetNewestFirst, closetItemViolations,
  closetListState, closetRowFace, closetEditPatch,
  closetSuggestState, closetMayOfferReview, closetNotListedSentence, closetTypeForConjureKind,
} from "../closet.js";
// CLOSET-1 — her order: send-to-cauldron from any closet item, merchandise refused warmly. The
// REFUSAL is the door's own sentence and is never composed here (only the server can read her
// catalog), and the LABEL is the shared sender's, in both languages.
import { SendToCauldronButton } from "./SendToCauldronButton";
import { photoAccept } from "../uploadTypes.js";
import { preparePhotoForUpload } from "../photoUpload.js";

export type ClosetItem = {
  id: number | string;
  name: string;
  type: string;
  source: string;
  note?: string;
  has_photo?: boolean;
  created_at?: string;
};

// One row of HER catalog, exactly as `server/db/conjure-catalog.js` shapes it. The type is declared
// beside the component that renders it (the house's standing rule), and it carries `kind` because
// the KIND is what tells a member which `| Halo` she is looking at when six of them share a label.
export type ConjureProduct = {
  name: string;
  nameKey: string;
  label: string;
  kind: string;
  description: string;
  productClass: string;
  imageUrl: string;
};

// R18 — THREE STATES, NOT TWO, AND THE MIDDLE ONE IS THE WHOLE POINT. `null` is "the read has not
// answered yet" and holds still; `[]` is an ANSWER and earns her empty line; a list is a list.
// Collapsing the first two shows "your closet is empty" to a member whose closet is full and whose
// network was slow — which is the one wrong thing to tell her about her own belongings (R64's
// cauldron ruling, one room over).
type Loaded = ClosetItem[] | null;

// **HER "PHOTO OR TYPE ICON" CLAUSE.** The table is keyed by the roster's own keys and an unrostered
// type gets a VISIBLY unknown mark rather than a plausible wrong one — `DRAWER_ICONS`' silent
// Sparkles fallback is the defect this same round repaired one rail over, and it is not repeated
// here. `closetRowFace` decides; this only draws.
const TYPE_ICONS: Record<string, JSX.Element> = {
  oil: <Droplet size={18} strokeWidth={1.5} />,
  candle: <Flame size={18} strokeWidth={1.5} />,
  herb: <Leaf size={18} strokeWidth={1.5} />,
  root: <Sprout size={18} strokeWidth={1.5} />,
  crystal: <Gem size={18} strokeWidth={1.5} />,
  seal: <Stamp size={18} strokeWidth={1.5} />,
  incense: <Wind size={18} strokeWidth={1.5} />,
  "altar-piece": <Church size={18} strokeWidth={1.5} />,
  tool: <Wrench size={18} strokeWidth={1.5} />,
  other: <Package size={18} strokeWidth={1.5} />,
  unknown: <HelpCircle size={18} strokeWidth={1.5} />,
};

export function ClosetBody({ doFetch = fetch, language }: { doFetch?: typeof fetch; language: string }) {
  const [items, setItems] = useState<Loaded>(null);
  const [failed, setFailed] = useState(false);
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");

  // Her three screens plus the add flow's first step. `detail` carries the item she tapped.
  const [view, setView] = useState<"list" | "source" | "form" | "detail">("list");
  const [openItem, setOpenItem] = useState<ClosetItem | null>(null);
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState("");
  const [type, setType] = useState(CLOSET_TYPES[0]?.key || "");
  const [source, setSource] = useState("own");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<{ filename: string; data: string } | null>(null);
  const [photoNote, setPhotoNote] = useState("");

  // ---- ITEM 3 CLAUSE 1: the live dropdown's own state -------------------------------------------
  // `null` is "not answered yet" and `[]` is "the catalog answered nothing", which is the same R18
  // split the list above already turns on — and here it is what decides whether her review control
  // may show at all. `closetSuggestState` owns the decision; these are only its inputs.
  const [suggestions, setSuggestions] = useState<ConjureProduct[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [suggestFailed, setSuggestFailed] = useState(false);
  const [notListed, setNotListed] = useState("");
  // Her browse path: the catalog's own KIND segments, then one shelf.
  const [browsing, setBrowsing] = useState(false);
  const [kinds, setKinds] = useState<{ kind: string; count: number }[] | null>(null);
  const [openKind, setOpenKind] = useState("");
  const [kindProducts, setKindProducts] = useState<ConjureProduct[] | null>(null);
  // >>> **HER SITE-WIDE LIST ITEM 5 — BULK ADD, AND IT IS A MODE RATHER THAN A CHANGE TO THE
  // TAP.** <<< A member who owns six of her products was doing open-browse, open-shelf, tap and
  // land-back-on-the-list six times, because `addProduct` returns her to the closet by her own
  // clause. That clause is untouched: an ordinary tap still adds one and still lands her on the
  // list holding it. `picking` is what she turns on to tick several instead.
  const [picking, setPicking] = useState(false);
  // The NAMES, because the name is what the bulk door takes and what the catalog resolves — a set
  // of product objects would be this screen deciding what her product is (R28/R29).
  const [picked, setPicked] = useState<string[]>([]);

  // D-5's law: the guard that decides is a REF, because a `useState` guard reads the value from the
  // render that BOUND the handler, so two taps inside one frame both see `false` and both send. The
  // state survives as what the button WEARS, so the refusal is visible rather than swallowed.
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const answer = await doFetch("/api/closet", { credentials: "include" }).then((r) => r.json());
      if (answer?.ok && Array.isArray(answer.items)) { setItems(answer.items); setFailed(false); return; }
      setFailed(true);
    } catch { setFailed(true); }
  }, [doFetch]);

  useEffect(() => { void load(); }, [load]);

  // The search and the order are the SHARED law's, run over what she already has rather than a
  // second round trip: her closet is her own small list, and a server search here would be a second
  // predicate beside `closetSearch` (R28/R29).
  const shown = useMemo(() => closetNewestFirst(closetSearch(items || [], term)), [items, term]);
  const listState = closetListState({ items, failed, matches: shown.length });

  const resetForm = () => {
    setName(""); setType(CLOSET_TYPES[0]?.key || ""); setNote(""); setPhoto(null); setPhotoNote(""); setStatus("");
    setSuggestions(null); setSearching(false); setSuggestFailed(false); setNotListed("");
    setBrowsing(false); setOpenKind(""); setKindProducts(null);
    // A selection left behind is a selection that re-appears over a different shelf.
    setPicking(false); setPicked([]);
  };
  const toList = () => { setView("list"); setOpenItem(null); setEditing(false); resetForm(); };

  // HER CONJURE PATH FILES FOR REVIEW AND DOES NOT WRITE A ROW, WHICH IS THE LAW'S OWN RULING.
  // There is no Conjure product catalog in this database (measured: 913 prose chunks under three
  // document titles, no pictures), so a Conjure item is a NAME SHE TYPED that the house has not
  // checked yet. Filing it as owned would be the machine inventing a catalog entry — exactly what
  // her "not listed" clause forbids members from doing, so the house may not do it either.
  const conjure = source === "conjure";

  // ---- ITEM 3 CLAUSE 1: THE LIVE DROPDOWN -------------------------------------------------------
  //
  // **IT IS DEBOUNCED AND IT IS SEQUENCED, AND THE SECOND ONE IS THE PART THAT MATTERS.** A member
  // typing `halo` fires four overlapping requests and they can come back in any order, so a stale
  // answer for `hal` can land AFTER the answer for `halo` and paint the wrong list under a box that
  // now says something else. The `seq` ref makes every answer name the request it belongs to, and an
  // answer that is not the newest is DROPPED rather than rendered.
  //
  // **AND `setSearching(true)` FIRES BEFORE THE WAIT, NOT AFTER IT.** Between a keystroke and the
  // request there is a 180ms window, and a box that reads "nothing matches" in that window is the
  // exact collapse `closetSuggestState`'s five states exist to prevent — with the added cost here
  // that "nothing matches" is what unlocks her review control.
  const seq = useRef(0);
  useEffect(() => {
    if (!(view === "form" && conjure) || browsing) return;
    const typed = name.trim();
    setNotListed("");
    if (typed.length < CLOSET_SUGGEST_MIN) { setSuggestions(null); setSearching(false); setSuggestFailed(false); return; }
    const mine = seq.current + 1;
    seq.current = mine;
    setSearching(true);
    setSuggestFailed(false);
    const timer = setTimeout(async () => {
      try {
        const answer = await doFetch(`/api/closet/suggest?q=${encodeURIComponent(typed)}`, { credentials: "include" }).then((r) => r.json());
        if (seq.current !== mine) return;
        if (answer?.ok && Array.isArray(answer.products)) { setSuggestions(answer.products); setSuggestFailed(false); }
        else setSuggestFailed(true);
      } catch { if (seq.current === mine) setSuggestFailed(true); }
      finally { if (seq.current === mine) setSearching(false); }
    }, 180);
    return () => clearTimeout(timer);
  }, [name, view, conjure, browsing, doFetch]);

  const suggestState = closetSuggestState({ typed: name, results: suggestions, searching, failed: suggestFailed });

  // Her browse path's shelves, fetched once when she opens it. `kinds === null` holds still; the
  // catalog answering an empty roster is a different fact and gets its own line.
  useEffect(() => {
    if (!browsing || kinds !== null) return;
    let alive = true;
    void (async () => {
      try {
        const answer = await doFetch("/api/closet/suggest?kinds=1", { credentials: "include" }).then((r) => r.json());
        if (alive) setKinds(answer?.ok && Array.isArray(answer.kinds) ? answer.kinds : []);
      } catch { if (alive) setKinds([]); }
    })();
    return () => { alive = false; };
  }, [browsing, kinds, doFetch]);

  async function openShelf(kind: string) {
    setOpenKind(kind);
    setKindProducts(null);
    // Ticks belong to the shelf they were made on. Carrying them across would add products she
    // chose on a shelf she has left, which is the one mistake this whole mode must not make.
    setPicked([]);
    try {
      const answer = await doFetch(`/api/closet/suggest?kind=${encodeURIComponent(kind)}`, { credentials: "include" }).then((r) => r.json());
      setKindProducts(answer?.ok && Array.isArray(answer.products) ? answer.products : []);
    } catch { setKindProducts([]); }
  }

  /**
   * **HER CLAUSE: "Tap one, it is added, and it APPEARS IMMEDIATELY in the closet list with
   * confirmation."** All three halves are here and every one is load-bearing: the reload is AWAITED
   * before the screen changes so she lands on a list that already holds it, the search term is
   * cleared so a filter she left in the box cannot hide the thing she just added (R43's M8 on her own
   * screen), and the confirmation is the SERVER'S sentence rather than one composed here — the route
   * stored the catalog's own spelling of the name, and a client-composed line would announce the
   * name she typed instead of the product she got.
   */
  async function addProduct(product: ConjureProduct) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const answer = await doFetch("/api/closet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // The type rides so the body is complete, and the SERVER re-derives it through the same
        // shared law anyway — a client that sent something else is corrected, not believed.
        body: JSON.stringify({ name: product.name, type: closetTypeForConjureKind(product.kind), source: "conjure", note: "" }),
      }).then((r) => r.json());
      if (!answer?.ok) {
        if (answer?.notListed) { setNotListed(String(answer.name || product.name)); setStatus(answer.message || ""); return; }
        setStatus(answer?.message || "That could not be saved just now.");
        return;
      }
      await load();
      setTerm("");
      toList();
      setStatus(answer.message || "");
    } catch { setStatus("That could not be saved just now."); }
    finally { inFlight.current = false; setBusy(false); }
  }

  /**
   * **HER "NOT LISTED" PATH — never a silent swallow.** This is the ONLY thing that reaches
   * `/api/closet/review`, and it is reachable from exactly one state: the catalog answered and holds
   * nothing like it (`closetMayOfferReview`). Offering it while a request is still out would file a
   * real product onto the owner's desk as missing, every time the network was slow.
   */
  async function fileForReview() {
    if (inFlight.current) return;
    const typed = name.trim();
    if (!typed) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const answer = await doFetch("/api/closet/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: typed, note }),
      }).then((r) => r.json());
      if (!answer?.ok) { setStatus(answer?.message || "That could not be sent just now."); return; }
      setNotListed(typed);
      setStatus(closetNotListedSentence(typed));
      setName("");
      setSuggestions(null);
    } catch { setStatus("That could not be sent just now."); }
    finally { inFlight.current = false; setBusy(false); }
  }

  /** Tick or un-tick one product. Bounded HERE as well as at the door, so a member is told at the
   *  moment she exceeds the cap rather than after composing a selection the server will refuse. */
  function togglePick(productName: string) {
    const key = String(productName || "").trim();
    if (!key) return;
    setPicked((was) => {
      if (was.includes(key)) return was.filter((n) => n !== key);
      if (was.length >= CLOSET_BULK_MAX) {
        setStatus(`That is as many as you can add at once. Add these ${CLOSET_BULK_MAX} first.`);
        return was;
      }
      setStatus("");
      return [...was, key];
    });
  }

  /**
   * **THE BULK ADD, AND ITS ANSWER IS THE SERVER'S SENTENCE.** Every item's outcome is decided
   * there and summarised there (`closetBulkSummary`), so this composes nothing: a line written
   * here would announce what she ticked rather than what she got, which is exactly the defect
   * `addProduct` already carries a paragraph about one function up.
   */
  async function addPicked() {
    if (inFlight.current || !picked.length) return;
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const answer = await doFetch("/api/closet/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ names: picked }),
      }).then((r) => r.json());
      // R39/R47: these routes REFUSE with HTTP 200 and `{ ok: false }`. Read the payload.
      if (!answer?.ok) { setStatus(answer?.message || "Those could not be saved just now."); return; }
      // **HER CLAUSE, KEPT FOR THE BATCH:** the reload is AWAITED before the screen changes, so she
      // lands on a list that already holds them, and the search term is cleared so a filter she
      // left in the box cannot hide what she just added.
      await load();
      setTerm("");
      const sentence = String(answer.message || "");
      // **NOTHING ADDED MEANS SHE STAYS WHERE SHE IS.** Landing her on the closet list after a run
      // that stored nothing would read as success and leave her hunting for items that are not
      // there — she stays on the shelf, with the sentence, and her ticks intact to try again.
      if (Number(answer.added) > 0) { toList(); }
      else { setPicking(true); }
      setStatus(sentence);
    } catch { setStatus("Those could not be saved just now."); }
    finally { inFlight.current = false; setBusy(false); }
  }

  async function pickPhoto(file: File | null | undefined) {
    if (!file) { setPhoto(null); setPhotoNote(""); return; }
    try {
      // Resized on the device before a byte leaves it, the same law her avatar upload obeys — a
      // 9.6MB phone photo is a 12.86MB base64 body and the route caps it. `preparePhotoForUpload`
      // hands back the base64 PAYLOAD with no `data:` prefix, which is exactly what the route's
      // `Buffer.from(str, "base64")` needs, and it FALLS BACK to the original bytes rather than
      // throwing when a browser cannot decode the file.
      const prepared = await preparePhotoForUpload(file);
      if (!prepared?.base64) { setPhoto(null); setPhotoNote("That photo could not be read. A JPEG or PNG works."); return; }
      setPhoto({ filename: prepared.filename || file.name || "item.jpg", data: prepared.base64 });
      setPhotoNote(file.name || "Photo attached");
    } catch {
      setPhoto(null);
      setPhotoNote("That photo could not be read. A JPEG or PNG works.");
    }
  }

  async function submit() {
    if (inFlight.current) return;             // D-5: her words are still in the box
    // **THE CONJURE PATH NOW GOES THROUGH THE SAME ADD ROUTE AS EVERYTHING ELSE, AND THIS BRANCH IS
    // WHAT ENTER MEANS ON THAT BOX.** Before ITEM 3b it posted straight to `/api/closet/review`,
    // because there was no catalog to resolve against; now a typed name is a real add attempt, and
    // only a name that resolves to NOTHING reaches her review — through `fileForReview`, from the one
    // state that may offer it. A member who types her product exactly and presses Enter gets it.
    if (conjure) {
      const typed = name.trim();
      if (!typed) { setStatus("Give it a name first."); return; }
      const exact = (suggestions || []).find((p) => p.name.toLowerCase() === typed.toLowerCase());
      await addProduct(exact || ({ name: typed, kind: "" } as ConjureProduct));
      return;
    }
    const candidate = { name, type, source, note };
    const violations = closetItemViolations(candidate);
    if (violations.length) {
      setStatus(violations.includes("name-empty") ? "Give it a name first." : "That is not something the closet can hold yet.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const answer = await doFetch("/api/closet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...candidate, photo }),
      }).then((r) => r.json());
      // R39/R47's law: these routes REFUSE with HTTP 200 and `{ ok: false }`, so a client reading the
      // status code calls a refusal a success. Read the payload.
      if (!answer?.ok) { setStatus(answer?.message || "That could not be saved just now."); return; }
      // HER CLAUSE: **Save returns to the list with the item visible.** The reload is AWAITED before
      // the screen changes, so she lands on a list that already has it rather than on one that grows
      // a row a moment later — and the search term is cleared, because a filter she left on the box
      // is exactly what would hide the thing she just saved (R43's M8, on her own screen).
      await load();
      setTerm("");
      toList();
      setStatus(answer.message || "");
    } catch {
      setStatus("That could not be saved just now.");
    } finally { inFlight.current = false; setBusy(false); }
  }

  async function saveEdit() {
    if (!openItem || inFlight.current) return;
    const patch = closetEditPatch({ name, type, note });
    const violations = closetItemViolations({ ...patch, source: openItem.source });
    if (violations.length) {
      setStatus(violations.includes("name-empty") ? "Give it a name first." : "That is not something the closet can hold yet.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const answer = await doFetch(`/api/closet/${encodeURIComponent(String(openItem.id))}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(photo ? { ...patch, photo } : patch),
      }).then((r) => r.json());
      if (!answer?.ok) { setStatus(answer?.message || "That could not be saved just now."); return; }
      await load();
      toList();
    } catch { setStatus("That could not be saved just now."); }
    finally { inFlight.current = false; setBusy(false); }
  }

  async function remove(id: ClosetItem["id"]) {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const answer = await doFetch(`/api/closet/${encodeURIComponent(String(id))}`, { method: "DELETE", credentials: "include" }).then((r) => r.json());
      if (!answer?.ok) { setStatus(answer?.message || "That could not be deleted just now."); return; }
      await load();
      toList();
    } catch { setStatus("That could not be deleted just now."); }
    finally { inFlight.current = false; }
  }

  const face = (item: ClosetItem) => {
    const f = closetRowFace(item);
    if (f.kind === "photo") {
      return <span className="closet__thumb" style={{ backgroundImage: `url(/api/closet/photo/${encodeURIComponent(f.key)})` }} aria-hidden="true" />;
    }
    return <span className="closet__icon" aria-hidden="true">{TYPE_ICONS[f.key] || TYPE_ICONS.unknown}</span>;
  };

  // ---- THE FIELD BLOCK, ONE BODY FOR ADD AND FOR EDIT ------------------------------------------
  // Her "labeled fields" clause, and the labels are REAL <label>s rather than placeholders: a
  // placeholder disappears the moment she types, which is what made the old form unreadable.
  const fields = (
    <>
      <label className="closet__field">
        <span className="closet__label">{CLOSET_TYPE_LABEL}</span>
        <select className="closet__type" value={type} onChange={(e) => setType(e.target.value)}>
          {CLOSET_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </label>
      <label className="closet__field">
        <span className="closet__label">{CLOSET_NAME_LABEL}</span>
        <input className="closet__name" value={name} maxLength={CLOSET_NAME_MAX} onChange={(e) => setName(e.target.value)} />
      </label>
      {/* HER CLAUSE 2: the DESCRIPTION field, with the prompt IN THE BOX. The label is a real
          <label> and the prompt is the PLACEHOLDER, so the field still names itself once she has
          started typing and the prompt has gone. The encouragement below is a nudge and never a
          gate — `closetItemViolations` does not require it, because a member who will not describe
          her item still owns it. */}
      <label className="closet__field">
        <span className="closet__label">{CLOSET_DESCRIPTION_LABEL}</span>
        <textarea
          className="closet__note"
          value={note}
          maxLength={CLOSET_NOTE_MAX}
          rows={3}
          placeholder={CLOSET_DESCRIPTION_PROMPT}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="closet__hint">{CLOSET_DESCRIPTION_ENCOURAGE}</span>
      </label>
      {/* R15: the picker offers exactly what the sniffer accepts, from ONE declaration. */}
      <label className="closet__field">
        <span className="closet__label">{CLOSET_PHOTO_LABEL}</span>
        <input className="closet__photo" type="file" accept={photoAccept()} onChange={(e) => void pickPhoto(e.target.files?.[0])} />
        {photoNote ? <span className="closet__photo-note">{photoNote}</span> : null}
      </label>
    </>
  );

  return (
    <div className="closet" data-closet-view={view}>
      {/* ---- SCREEN ONE: exactly three things, and nothing else ---------------------------------- */}
      {view === "list" ? (
        <>
          <div className="closet__head">
            <input
              className="closet__search"
              type="search"
              value={term}
              placeholder={CLOSET_SEARCH_PLACEHOLDER}
              onChange={(e) => setTerm(e.target.value)}
              aria-label={CLOSET_SEARCH_PLACEHOLDER}
            />
          </div>

          <div className="closet__scroll">
            {listState === "waiting" ? <p className="closet__waiting">{CLOSET_WAITING_LINE}</p> : null}
            {listState === "failed" ? <p className="closet__status">{CLOSET_FAILED_LINE}</p> : null}
            {/* HER EMPTY STATE: one warm line, and the Add button below is the pinned one. */}
            {listState === "empty" ? <p className="closet__empty">{CLOSET_EMPTY_LINE}</p> : null}
            {listState === "no-match" ? <p className="closet__empty">{CLOSET_NO_MATCH_LINE}</p> : null}
            {status ? <p className="closet__status" role="status">{status}</p> : null}

            {listState === "list" ? (
              <ul className="closet__list">
                {shown.map((item) => (
                  <li className="closet__item" key={String(item.id)} data-closet-id={String(item.id)}>
                    {/* HER "ITEM TAP OPENS DETAIL" CLAUSE. The whole row is the control, so the tap
                        target is the row rather than a word inside it. */}
                    <button
                      className="closet__open"
                      type="button"
                      onClick={() => {
                        setOpenItem(item); setEditing(false); setStatus("");
                        setName(item.name); setType(item.type); setNote(item.note || "");
                        setPhoto(null); setPhotoNote("");
                        setView("detail");
                      }}
                    >
                      {face(item)}
                      <span className="closet__body">
                        <span className="closet__item-name">{item.name}</span>
                        <span className="closet__meta">{closetTypeLabel(item.type)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {/* HER THIRD THING: one gold Add, PINNED at the bottom. */}
          <div className="closet__dock">
            <button className="closet__add-btn" type="button" onClick={() => { resetForm(); setView("source"); }}>
              {CLOSET_ADD_LABEL}
            </button>
          </div>
        </>
      ) : null}

      {/* ---- BEHIND ADD, STEP ONE: her two choices ---------------------------------------------- */}
      {view === "source" ? (
        <div className="closet__panel">
          <h3 className="closet__panel-title">{CLOSET_SOURCE_STEP_TITLE}</h3>
          <div className="closet__choices">
            {CLOSET_SOURCE_CHOICES.map((c) => (
              <button key={c.source} className="closet__choice" type="button"
                onClick={() => { setSource(c.source); setView("form"); }}>{c.label}</button>
            ))}
          </div>
          <div className="closet__dock">
            <button className="closet__back" type="button" onClick={toList}>{CLOSET_BACK_LABEL}</button>
          </div>
        </div>
      ) : null}

      {/* ---- BEHIND ADD, STEP TWO: the labeled fields ------------------------------------------- */}
      {view === "form" ? (
        <div className="closet__panel">
          <h3 className="closet__panel-title">{CLOSET_FORM_TITLE}</h3>
          <p className="closet__from">{closetSourceLabel(source)}</p>
          <form className="closet__form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            {/* ---- HER CENTREPIECE: the smart path ------------------------------------------- */}
            {conjure ? (
              <div className="closet__conjure">
                {browsing ? (
                  /* ---- THE BROWSE PATH, "for anyone who cannot find a thing by typing" ------- */
                  <div className="closet__browse">
                    <div className="closet__browse-head">
                      <h4 className="closet__browse-title">{openKind || CLOSET_BROWSE_TITLE}</h4>
                      {/* HER ITEM 5's ONE CONTROL. It shows only on an open shelf, because a
                          selection has no meaning over the list of shelves. */}
                      {openKind ? (
                        <button
                          className="closet__bulk-toggle"
                          type="button"
                          aria-pressed={picking}
                          onClick={() => { setPicking((was) => !was); setPicked([]); setStatus(""); }}
                        >{picking ? CLOSET_BULK_LEAVE : CLOSET_BULK_ENTER}</button>
                      ) : null}
                      <button
                        className="closet__back"
                        type="button"
                        onClick={() => { if (openKind) { setOpenKind(""); setKindProducts(null); setPicking(false); setPicked([]); } else setBrowsing(false); }}
                      >{openKind ? CLOSET_BACK_LABEL : CLOSET_BROWSE_BACK}</button>
                    </div>
                    {!openKind ? (
                      kinds === null
                        ? <p className="closet__waiting">{CLOSET_SUGGEST_SEARCHING}</p>
                        : (
                          <ul className="closet__shelves">
                            {kinds.map((k) => (
                              <li key={k.kind}>
                                <button className="closet__shelf" type="button" data-closet-kind={k.kind} onClick={() => void openShelf(k.kind)}>
                                  <span className="closet__shelf-name">{k.kind}</span>
                                  <span className="closet__shelf-count">{k.count}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )
                    ) : (
                      kindProducts === null
                        ? <p className="closet__waiting">{CLOSET_SUGGEST_SEARCHING}</p>
                        : (
                          <>
                          <ul className="closet__suggestions">
                            {kindProducts.map((p) => (
                              <li key={p.nameKey}>
                                {/* ONE BUTTON, TWO MEANINGS, AND THE MODE IS WHAT DECIDES — never two
                                    tap targets in a row on a phone. Out of the mode this is her
                                    original tap-to-add, unchanged. */}
                                <button
                                  className={`closet__suggestion${picking && picked.includes(p.name) ? " is-picked" : ""}`}
                                  type="button"
                                  disabled={busy}
                                  aria-pressed={picking ? picked.includes(p.name) : undefined}
                                  data-closet-product={p.nameKey}
                                  onClick={() => (picking ? togglePick(p.name) : void addProduct(p))}
                                >
                                  <span className="closet__suggestion-name">{p.label || p.name}</span>
                                  {p.kind ? <span className="closet__suggestion-kind">{p.kind}</span> : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                          {picking ? (
                            <div className="closet__bulk-bar">
                              {picked.length === 0
                                ? <p className="closet__bulk-none">{CLOSET_BULK_NONE}</p>
                                : (
                                  <button className="closet__bulk-add" type="button" disabled={busy}
                                    onClick={() => void addPicked()}>{closetBulkAddLabel(picked.length)}</button>
                                )}
                            </div>
                          ) : null}
                          </>
                        )
                    )}
                  </div>
                ) : (
                  <>
                    <label className="closet__field">
                      <span className="closet__label">{CLOSET_NAME_LABEL}</span>
                      <input
                        className="closet__name"
                        value={name}
                        maxLength={CLOSET_NAME_MAX}
                        placeholder={CLOSET_CONJURE_PLACEHOLDER}
                        autoComplete="off"
                        onChange={(e) => setName(e.target.value)}
                      />
                    </label>

                    {/* THE DROPDOWN, address-autocomplete style, and every one of its five states
                        says something DIFFERENT. `searching` and `failed` are deliberately not the
                        same picture as `none`, because `none` is the only one that may offer her
                        review — a slow network must never file a real product as missing. */}
                    <div className="closet__suggest" data-closet-suggest={suggestState}>
                      {suggestState === "searching" ? <p className="closet__waiting">{CLOSET_SUGGEST_SEARCHING}</p> : null}
                      {suggestState === "failed" ? <p className="closet__status">{CLOSET_FAILED_LINE}</p> : null}
                      {suggestState === "results" ? (
                        <ul className="closet__suggestions">
                          {(suggestions || []).map((p) => (
                            <li key={p.nameKey}>
                              <button className="closet__suggestion" type="button" disabled={busy}
                                data-closet-product={p.nameKey} onClick={() => void addProduct(p)}>
                                <span className="closet__suggestion-name">{p.label || p.name}</span>
                                {p.kind ? <span className="closet__suggestion-kind">{p.kind}</span> : null}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {closetMayOfferReview(suggestState) && !notListed ? (
                        <div className="closet__not-listed">
                          <p className="closet__empty">{CLOSET_NO_PRODUCT_LINE}</p>
                          <button className="closet__review" type="button" disabled={busy} onClick={() => void fileForReview()}>
                            {CLOSET_NOT_LISTED_LABEL}
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <button className="closet__browse-open" type="button" onClick={() => setBrowsing(true)}>
                      {CLOSET_BROWSE_LABEL}
                    </button>
                  </>
                )}
              </div>
            ) : fields}
            {status ? <p className="closet__status" role="status">{status}</p> : null}
            <div className="closet__dock">
              <button className="closet__back" type="button" onClick={() => setView("source")}>{CLOSET_BACK_LABEL}</button>
              {/* The Conjure path's action is the TAP on a product; Enter still means "add what I
                  typed", so the button stays and says the same thing. */}
              <button className="closet__add-btn" type="submit" disabled={busy}>{busy ? "Saving…" : CLOSET_SAVE_LABEL}</button>
            </div>
          </form>
        </div>
      ) : null}

      {/* ---- THE DETAIL, WITH EDIT AND DELETE --------------------------------------------------- */}
      {view === "detail" && openItem ? (
        <div className="closet__panel">
          {editing ? (
            <>
              <h3 className="closet__panel-title">{CLOSET_EDIT_LABEL}</h3>
              {fields}
              {status ? <p className="closet__status" role="status">{status}</p> : null}
              <div className="closet__dock">
                <button className="closet__back" type="button" onClick={() => setEditing(false)}>{CLOSET_BACK_LABEL}</button>
                <button className="closet__add-btn" type="button" disabled={busy} onClick={() => void saveEdit()}>{busy ? "Saving…" : CLOSET_SAVE_LABEL}</button>
              </div>
            </>
          ) : (
            <>
              <div className="closet__detail-head">
                {face(openItem)}
                <div>
                  <h3 className="closet__panel-title">{openItem.name}</h3>
                  <p className="closet__meta">{closetTypeLabel(openItem.type)} · {closetSourceLabel(openItem.source)}</p>
                </div>
              </div>
              {openItem.note ? <p className="closet__item-note">{openItem.note}</p> : null}
              {status ? <p className="closet__status" role="status">{status}</p> : null}
              <div className="closet__dock">
                <button className="closet__back" type="button" onClick={toList}>{CLOSET_BACK_LABEL}</button>
                {/* CLOSET-1 — **THE SHARED SENDER, NOT A SECOND TRANSPORT** (R50's own finding: the
                    one surface that hand-typed this POST hand-typed three English refusals with it).
                    It carries the ITEM ID and nothing else: the door reads her row, decides on
                    ITEM 3b's own product class, and refuses merchandise in its own warm words — so
                    this side cannot classify, cannot invent a material and cannot disagree. */}
                <SendToCauldronButton language={language} kind="closet-item" title={openItem.name}
                  data={{ itemId: String(openItem.id) }} />
                <button className="closet__edit" type="button" onClick={() => { setEditing(true); setStatus(""); }}>{CLOSET_EDIT_LABEL}</button>
                <button className="closet__delete" type="button" onClick={() => void remove(openItem.id)}>{CLOSET_DELETE_LABEL}</button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
